import { useLazyGetUserFilterByIdQuery } from 'store/services/flow/userFilters';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { setDefaultFiltersOnlyIfNoFilterApplied } from 'store/entities/drawer/actions';
import type { FilterColumnValue, FilterValue, GetUserFiltersResWithContext } from 'types/userFilters';
import { isValueEmpty } from 'utils/filterFunctions';

interface UseApplyDefaultFiltersProps {
  savedFilters?: GetUserFiltersResWithContext;
  viewId: string;
  isOverview: boolean;
  type?: 'userAgenda' | 'table';
}

type FilterItem = Partial<FilterValue> & Partial<FilterColumnValue>;

export const useApplyDefaultFilters = ({ savedFilters, viewId, isOverview, type = 'userAgenda' }: UseApplyDefaultFiltersProps) => {
  const dispatch = useDispatch();
  const [getFilterById] = useLazyGetUserFilterByIdQuery();

  const applyDefaultFilters = useCallback(async () => {
    if (!savedFilters?.filters?.items?.length) return;

    if (!savedFilters.viewId || savedFilters.viewId !== viewId) return;

    for (const item of savedFilters.filters.items) {
      if (!item.isDefault) continue;

      const { data } = await getFilterById({ userFilterId: item.userFilterId });

      if (!data) continue;

      const rawValues = type === 'table' ? data.columnValues : data.values;

      if (!rawValues) continue;

      // values is not actually values, but an array of filtering formItems (type FilterValue)
      // which then have their own value, values, fromValue, toValue properties
      const filtersToSet = rawValues.map((rawVal) => {
        const val = rawVal as FilterItem;
        const formItemType = (type === 'table' ? val.columnType : val.formItemType) || 'text';
        const isArray = Array.isArray(val.values);
        const hasValues = isArray && val.values && val.values.length > 0;
        const hasRangeValues = !isValueEmpty(val.fromValue) || !isValueEmpty(val.toValue);

        const getDateValuesLegacy = (dates?: string[]) => {
          const validDates =
            dates?.map((date) => (date && !isNaN(Date.parse(date)) ? date : new Date().toISOString().split('T')[0])).filter(Boolean) || [];

          if (validDates.length === 1) {
            return {
              fromDate: validDates[0],
              toDate: validDates[0],
            };
          }

          return validDates.join(', ');
        };

        const getDateValues = (fromDate?: string, toDate?: string) => {
          const validFromDate = fromDate && !isNaN(Date.parse(fromDate)) ? fromDate : new Date().toISOString().split('T')[0];
          const validToDate = toDate && !isNaN(Date.parse(toDate)) ? toDate : new Date().toISOString().split('T')[0];

          return {
            fromDate: validFromDate,
            toDate: validToDate,
          };
        };

        const getNumberValuesLegacy = (numbers?: string[]) => {
          if (numbers?.length === 1) {
            const numValue = parseFloat(numbers[0]);
            if (!isNaN(numValue)) {
              return {
                fromNumber: numValue,
                toNumber: numValue,
              };
            }
          }

          return numbers?.join(', ');
        };

        const getNumberValues = (fromNumber?: string, toNumber?: string) => {
          const validFromNumber = fromNumber && !isNaN(parseFloat(fromNumber)) ? parseFloat(fromNumber) : 0;
          const validToNumber = toNumber && !isNaN(parseFloat(toNumber)) ? parseFloat(toNumber) : 0;

          return {
            fromNumber: validFromNumber,
            toNumber: validToNumber,
          };
        };

        const getSelectValues = (values?: string[]) =>
          values?.map((v) => ({
            label: v,
            value: v,
          }));

        const getTextValues = (values?: string[]) => values?.join(', ');

        const filterValue:
          | string
          | { label: string; value: string }[]
          | { fromDate: string; toDate: string }
          | { fromNumber: number; toNumber: number }
          | undefined = (() => {
          if (hasRangeValues) {
            switch (formItemType) {
              // any nested list input in filters has "Records" type
              case 'Records':
                if (Number(val.fromValue) || Number(val.toValue)) {
                  return getNumberValues(val.fromValue, val.toValue);
                }
                return getDateValues(val.fromValue, val.toValue);
              case 'Date':
                return getDateValues(val.fromValue, val.toValue);
              case 'Number':
                return getNumberValues(val.fromValue, val.toValue);
              default:
                return hasValues ? getTextValues(val.values) : '';
            }
          }

          switch (formItemType) {
            case 'Date':
              return hasValues ? getDateValuesLegacy(val.values) : undefined;
            case 'Number':
              return hasValues ? getNumberValuesLegacy(val.values) : undefined;
            case 'Tables':
            case 'Select':
              return hasValues ? getSelectValues(val.values) : [];
            case 'Text':
            default:
              return hasValues ? getTextValues(val.values) : '';
          }
        })();

        return {
          formItemId: (type === 'table' ? val.columnId : val.formItemId) as string,
          formItemType,
          value: filterValue,
          referencedFormItemId: val.referencedFormItemId,
        };
      });

      dispatch(setDefaultFiltersOnlyIfNoFilterApplied({ [`${type}-${isOverview ? 'overview' : 'listing'}-${viewId}`]: filtersToSet ?? [] }));
      break;
    }
  }, [dispatch, getFilterById, isOverview, savedFilters, type, viewId]);

  return { applyDefaultFilters };
};
